import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { Helmet } from 'react-helmet';
import UserTypeSelector from './components/UserTypeSelector';
import JobSeekerForm from './components/JobSeekerForm';
import CompanyAdminForm from './components/CompanyAdminForm';
import SocialRegistration from './components/SocialRegistration';
import RegistrationBenefits from './components/RegistrationBenefits';

export default function Register() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [userType, setUserType] = useState('job_seeker');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleRegister = async (formData) => {
    setError('');
    setLoading(true);

    try {
      const { email, password, fullName, role } = formData;
      
      const { data, error: signUpError } = await signUp(
        email,
        password,
        fullName,
        role || userType,
        ''
      );
      
      if (signUpError) {
        setError(signUpError?.message || 'Failed to create account. Please try again.');
        return;
      }

      if (data?.user) {
        // Check if email confirmation is required
        if (data?.user?.identities?.length === 0) {
          // Email confirmation required
          setSuccess(true);
        } else {
          // Email confirmed or confirmation disabled, redirect to role selection
          navigate('/select-role');
        }
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialRegister = async (provider) => {
    setError('');
    setLoading(true);

    try {
      // Social registration will redirect to role selection
      const { data, error: oauthError } = await authService?.signInWithSocial(
        provider,
        '/select-role',
        userType
      );

      if (oauthError) {
        setError(oauthError?.message || `Failed to register with ${provider}`);
        setLoading(false);
      }
      // Note: User will be redirected to OAuth provider, loading state persists
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <Helmet>
          <title>Check Your Email - Recrutees</title>
        </Helmet>
        <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 text-green-600">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
                Check your email
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                We have sent you a confirmation link to verify your email address.
                Please check your inbox and click the link to complete your registration.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/login')}
                  className="inline-flex justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                >
                  Go to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Register - Recrutees</title>
      </Helmet>
      <div className="flex min-h-screen">
        {/* Left side - Registration Form */}
        <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div className="mb-8">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                Create your account
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Join Recrutees and start your journey
              </p>
            </div>

            {error && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <UserTypeSelector
              selectedType={userType}
              onTypeChange={setUserType}
              onTypeSelect={setUserType}
            />

            {userType === 'job_seeker' ? (
              <JobSeekerForm onSubmit={handleRegister} loading={loading} />
            ) : (
              <CompanyAdminForm onSubmit={handleRegister} loading={loading} />
            )}

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500">Or register with</span>
                </div>
              </div>

              <SocialRegistration onSocialRegister={handleSocialRegister} loading={loading} />
            </div>

            <p className="mt-6 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>

        {/* Right side - Benefits */}
        <RegistrationBenefits userType={userType} />
      </div>
    </>
  );
}